import Footer from "../components/footer";
import LandingPageComp from "../components/landing_page_comp";
import NavBar from "../components/navBar";
import PricingSection from "../components/pricingCard";
import Sports from "../components/sports_landing";
import TopNews from "../components/TopNews";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import { ArrowRightIcon, ShoppingBagIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

function LandingPage() {
  const isAuthenticated = localStorage.getItem("token") !== null;
  const navigate = useNavigate();
  const [loadingComponents, setLoadingComponents] = useState(
    new Set(["initial"])
  );

  useEffect(() => {
    document.documentElement.style.scrollSnapType = "none";

    const timer = setTimeout(() => {
      setLoadingComponents((prev) => {
        const newSet = new Set(prev);
        newSet.delete("initial");
        return newSet;
      });
    }, 1000);

    return () => {
      document.documentElement.style.scrollSnapType = "none";
      clearTimeout(timer);
    };
  }, []);

  const handleLoadingChange = useCallback((componentId, isLoading) => {
    setLoadingComponents((prevState) => {
      const newState = new Set(prevState);
      if (isLoading) {
        newState.add(componentId);
      } else {
        newState.delete(componentId);
      }
      return newState;
    });
  }, []);

  const onNewsLoadingChange = useCallback(
    (isLoading) => handleLoadingChange("news", isLoading),
    [handleLoadingChange]
  );
  const onSportsLoadingChange = useCallback(
    (isLoading) => handleLoadingChange("sports", isLoading),
    [handleLoadingChange]
  );
  const onPricingLoadingChange = useCallback(
    (isLoading) => handleLoadingChange("pricing", isLoading),
    [handleLoadingChange]
  );

  const isLoading = useMemo(
    () => loadingComponents.size > 0,
    [loadingComponents]
  );

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("desac-home-navigate", { detail: isAuthenticated })
    );
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isLoading) return undefined;
    const timeout = setTimeout(() => {
      setLoadingComponents(new Set());
    }, 12000);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-surface-950 pt-16 text-white md:pt-0">
      <div
        className={`fixed inset-0 z-[2000] flex items-center justify-center bg-surface-950 transition-all duration-300 ${
          isLoading ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <div className="flex flex-col items-center">
          <div role="status" aria-label="Loading home page" className="h-12 w-12 animate-spin rounded-full border-4 border-surface-600 border-t-accent"></div>
        </div>
      </div>

      <NavBar />

      {!isAuthenticated && <LandingPageComp />}

      {isAuthenticated && (
        <section className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-center lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent mb-3">YOUR ENTERTAINMENT HUB</p>
              <h1 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
                Stay in the moment with DESAC TV.
              </h1>
              <p className="mt-3 max-w-xl text-sm leading-6 text-gray-400 sm:text-base">
                Catch up on the latest stories, follow the action, and take part in the conversation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => navigate("/sports")} className="btn-secondary group gap-2">
                <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                Explore sports
                <ArrowRightIcon aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button type="button" onClick={() => navigate("/buy-votes")} className="btn-primary group gap-2">
                <ShoppingBagIcon aria-hidden="true" className="h-4 w-4" />
                Buy votes
              </button>
            </div>
          </div>
        </section>
      )}

      <div className="space-y-6 py-6 sm:space-y-8 sm:py-8">
        <TopNews onLoadingChange={onNewsLoadingChange} />
        <Sports onLoadingChange={onSportsLoadingChange} />
        <PricingSection onLoadingChange={onPricingLoadingChange} />
      </div>
      <Footer onLoadingChange={(isLoading) => handleLoadingChange("footer", isLoading)} />
    </main>
  );
}

export default LandingPage;
